import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useBlocker } from 'react-router-dom';
import { intlMessage } from 'constants/propTypes';

const messages = defineMessages({
  fallback: {
    id: 'prompt.fallback',
    defaultMessage: 'You have unsaved changes. Your changes will not be saved if you navigate off this screen.',
  },
});

function Prompt({ when, message }) {
  const intl = useIntl();

  const getText = useCallback(() => {
    return intl.formatMessage(message || messages.fallback);
  }, [message, intl]);

  const handleBeforeUnload = useCallback(
    event => {
      if (!when) return;

      event.preventDefault();
      // For browser refresh/close, we have to use the browser's native dialog
      // Modern browsers ignore custom messages and show generic "Leave site?" dialog
      event.returnValue = true; // Just need truthy value to trigger dialog
    },
    [when]
  );

  // Handle browser refresh/close (only for actual page unload)
  useEffect(() => {
    if (when) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, handleBeforeUnload]);

  // Handle route navigation with useBlocker
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        if (!when) return false;

        // Block navigation if we're navigating to a different route
        return currentLocation.pathname !== nextLocation.pathname;
      },
      [when]
    )
  );

  // Handle the blocked navigation by showing a confirmation dialog
  useEffect(() => {
    if (blocker.state === 'blocked') {
      // eslint-disable-next-line no-alert
      const shouldProceed = window.confirm(getText());
      if (shouldProceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, getText]);

  return null;
}

Prompt.propTypes = {
  when: PropTypes.bool.isRequired,
  message: intlMessage,
};

export default Prompt;
